import { Alert, AlertColor, Button, CircularProgress, Snackbar as MuiSnackbar } from "@mui/material";
import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";
import { usePhone } from "./usePhone";

// undoTimeout is how long a message with an undo button stays up
const undoTimeout = 5 * 1000;

// phoneBarHeight is the taller of the bars that can run along the top of a phone
// screen, in px: a dense toolbar (see MobileSheet)
const phoneBarHeight = 48;

type Message = {
    severity: AlertColor;
    text: string;
    loading: boolean;

    // action is a button at the end of the message, e.g. "Undo"
    action?: { label: string; onClick: () => void };
}

export const SnackContext = createContext({
    loading: (text?: string) => {},
    success: (text: string) => {},
    error: (text: string) => {},
    // undo shows text with an Undo button for a few seconds
    undo: (text: string, onUndo: () => void) => {},
    hide: () => {},
});

export function useSnackbar() {
    return useContext(SnackContext);
}

export function Snackbar(props: PropsWithChildren<{ timeout: number }>) {
    const phone = usePhone();
    const [message, setMessage] = useState<Message | null>(null);

    const ctx = useMemo(() => ({
        loading: (text?: string) => setMessage({ severity: "info", text: text || "Loading...", loading: true }),
        success: (text: string) => setMessage({ severity: "success", text: text, loading: false }),
        error: (text: string) => setMessage({ severity: "error", text: text, loading: false }),
        undo: (text: string, onUndo: () => void) => setMessage({
            severity: "success",
            text: text,
            loading: false,
            action: {
                label: "Undo",
                onClick: () => {
                    setMessage(null);
                    onUndo();
                },
            },
        }),
        hide: () => setMessage(null),
    }), []);

    let timeout: number | null = props.timeout;
    if (message?.loading) timeout = null;
    else if (message?.action) timeout = undoTimeout;

    return (
        <SnackContext.Provider value={ctx}>
            {props.children}
            <MuiSnackbar
                // a new message restarts the timer rather than inheriting the last one's
                key={message ? message.text + (message.action ? "-action" : "") : "none"}
                open={message !== null}
                autoHideDuration={timeout}
                // on a phone, the bottom of the screen is taken by the tab bar, the project
                // bar and the add button (see MobileTabBar, ProjectDrawer, NewTaskFab), and
                // the top row is the app bar or a sheet's bar with its back button (see
                // Layout, MobileSheet), so the message sits just under that bar
                anchorOrigin={phone ? { vertical: "top", horizontal: "center" } : undefined}
                sx={phone ? { top: `calc(${phoneBarHeight + 8}px + env(safe-area-inset-top))` } : undefined}
                onClose={(_, reason) => {
                    // a tap elsewhere shouldn't take the undo away before its time is up
                    if (reason === "clickaway" && message?.action) return;
                    setMessage(null);
                }}
            >
                <Alert
                    variant="filled"
                    severity={message?.severity || "info"}
                    icon={message?.loading ? <CircularProgress size={20} color="inherit" /> : undefined}
                    onClose={message?.loading || message?.action ? undefined : () => setMessage(null)}
                    action={message?.action && (
                        <Button color="inherit" size="small" onClick={message.action.onClick}>
                            {message.action.label}
                        </Button>
                    )}
                >
                    {message?.text}
                </Alert>
            </MuiSnackbar>
        </SnackContext.Provider>
    );
}
